import type { TooltipProps } from 'recharts';
import { CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { format } from 'date-fns';
import { Box, Paragraph, Tag, Text } from '@zillow/constellation-10';
import type { CustomerAnalyticsEnterpriseDailyAnalyticsDto } from '@zg-rentals/ts-customer-analytics-client';
import { localDateToDate } from '../../../../../utils/localDateToDate';

const STROKE_WIDTH = 2;

const LEGEND_MAPPING: Record<string, string> = {
  impressionCount: 'Total impressions',
  viewCount: 'Listing views',
  leadCount: 'Total leads',
  listingWebReferrals: 'Listing Web referrals',
  adClicks: 'Ad Clicks',
  addOnImpressions: 'Total impressions',
  addOnClickCount: 'Total clicks',
  addOnMetaImpressions: 'Meta impressions',
  addOnMetaClicks: 'Meta clicks',
  addOnTiktokImpressions: 'TikTok impressions',
  addOnTiktokClicks: 'TikTok clicks',
  regularBoostImpressions: 'Impressions',
  regularBoostClicks: 'Ad Clicks',
};

const LINE_COLORS: Record<string, string> = {
  impressionCount: '#A6E5FF',
  viewCount: '#0D4599',
  leadCount: '#17B036',
  listingWebReferrals: '#985DFF',
  adClicks: '#D63384',
  addOnImpressions: '#A6E5FF',
  addOnClickCount: '#D63384',
  addOnMetaImpressions: '#0D4599',
  addOnMetaClicks: '#17B036',
  addOnTiktokImpressions: '#985DFF',
  addOnTiktokClicks: '#FF6B35',
  regularBoostImpressions: '#A6E5FF',
  regularBoostClicks: '#D63384',
};

type GraphDataPoint = {
  reportDate: number;
  impressionCount: number;
  viewCount: number;
  leadCount: number;
  listingWebReferrals: number;
  adClicks: number;
  addOnImpressions: number;
  addOnClickCount: number;
  addOnMetaImpressions: number;
  addOnMetaClicks: number;
  addOnTiktokImpressions: number;
  addOnTiktokClicks: number;
  regularBoostImpressions: number;
  regularBoostClicks: number;
  isSpotlight: boolean;
};

type PropertyDetailGraphProps = {
  dailyBreakdowns: Array<CustomerAnalyticsEnterpriseDailyAnalyticsDto>;
  isExtend?: boolean;
  isBoost?: boolean;
};

export const PropertyDetailGraph = ({ dailyBreakdowns, isExtend, isBoost }: PropertyDetailGraphProps) => {
  const data: Array<GraphDataPoint> = dailyBreakdowns
    .filter((breakdown) => {
      if (isExtend) {
        return breakdown.addOnImpressions > 0 || breakdown.addOnClickCount > 0;
      }
      if (isBoost) {
        return breakdown.regularBoostImpressions > 0 || breakdown.regularBoostClicks > 0;
      }
      return (
        breakdown.impressionCount > 0 ||
        breakdown.viewCount > 0 ||
        breakdown.leadCount > 0 ||
        breakdown.listingWebReferrals > 0 ||
        breakdown.adClicks > 0
      );
    })
    .map((breakdown) => ({
      reportDate: localDateToDate(breakdown.reportDate).getTime(),
      impressionCount: breakdown.impressionCount,
      viewCount: breakdown.viewCount,
      leadCount: breakdown.leadCount,
      listingWebReferrals: breakdown.listingWebReferrals,
      adClicks: breakdown.adClicks,
      addOnImpressions: breakdown.addOnImpressions,
      addOnClickCount: breakdown.addOnClickCount,
      addOnMetaImpressions: breakdown.addOnMetaImpressions,
      addOnMetaClicks: breakdown.addOnMetaClicks,
      addOnTiktokImpressions: breakdown.addOnTiktokImpressions,
      addOnTiktokClicks: breakdown.addOnTiktokClicks,
      regularBoostImpressions: breakdown.regularBoostImpressions,
      regularBoostClicks: breakdown.regularBoostClicks,
      isSpotlight: (breakdown.totalDaysSpotlighted ?? 0) > 0,
    }))
    .sort((a, b) => a.reportDate - b.reportDate);

  const spotlightByLabel: Record<number, boolean> = {};
  data.forEach((dataPoint) => {
    spotlightByLabel[dataPoint.reportDate] = dataPoint.isSpotlight;
  });

  const longDateFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const renderTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (active && payload?.length && label) {
      const labelNum = typeof label === 'number' ? label : Number(label);
      return (
        <Box
          css={{
            backgroundColor: 'white',
            padding: '16px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            minWidth: '220px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box css={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Text css={{ fontWeight: 'bold', fontSize: '14px' }}>{longDateFormatter.format(new Date(labelNum))}</Text>
            {spotlightByLabel[labelNum] && (
              <Tag size="sm" tone="blue">
                {isExtend ? 'Listing' : 'Listing Spotlight'}
              </Tag>
            )}
          </Box>
          {payload.map((item) => {
            const color = LINE_COLORS[item.dataKey as string] || item.color || '#000';
            return (
              <Box
                key={item.name}
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingBlock: '4px',
                }}
              >
                <Box css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '2px',
                      backgroundColor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Text css={{ fontSize: '14px', color: '#666' }}>
                    {LEGEND_MAPPING[item.dataKey as string] || item.name}
                  </Text>
                </Box>
                <Text css={{ fontSize: '14px', fontWeight: 'bold', marginLeft: '16px' }}>
                  {typeof item.value === 'number' && item.value > -1 ? item.value.toLocaleString() : '-'}
                </Text>
              </Box>
            );
          })}
        </Box>
      );
    }
    return null;
  };

  const getLines = () => {
    if (isExtend) {
      return [
        { dataKey: 'addOnImpressions', color: LINE_COLORS.addOnImpressions },
        { dataKey: 'addOnClickCount', color: LINE_COLORS.addOnClickCount },
        { dataKey: 'addOnMetaImpressions', color: LINE_COLORS.addOnMetaImpressions },
        { dataKey: 'addOnMetaClicks', color: LINE_COLORS.addOnMetaClicks },
        { dataKey: 'addOnTiktokImpressions', color: LINE_COLORS.addOnTiktokImpressions },
        { dataKey: 'addOnTiktokClicks', color: LINE_COLORS.addOnTiktokClicks },
      ];
    }
    if (isBoost) {
      return [
        { dataKey: 'regularBoostImpressions', color: LINE_COLORS.regularBoostImpressions },
        { dataKey: 'regularBoostClicks', color: LINE_COLORS.regularBoostClicks },
      ];
    }
    return [
      { dataKey: 'impressionCount', color: LINE_COLORS.impressionCount },
      { dataKey: 'viewCount', color: LINE_COLORS.viewCount },
      { dataKey: 'leadCount', color: LINE_COLORS.leadCount },
      { dataKey: 'listingWebReferrals', color: LINE_COLORS.listingWebReferrals },
      { dataKey: 'adClicks', color: LINE_COLORS.adClicks },
    ];
  };

  const lines = getLines();

  if (data.length === 0) {
    return (
      <Box css={{ padding: '800', textAlign: 'center' }}>
        <Paragraph>No data available for the selected period.</Paragraph>
      </Box>
    );
  }

  return (
    <Box>
      <Paragraph css={{ marginBottom: '400', color: 'text.subtle' }}>
        Hover or click on the graph to check details.
        {!isExtend && !isBoost && ' Blue highlighted areas indicate Listing Spotlight was active.'}
      </Paragraph>
      <Box css={{ height: '300px', marginBlock: '400' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 20,
            }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              type="number"
              domain={['dataMin', 'dataMax']}
              scale="time"
              dy={10}
              dataKey="reportDate"
              tickLine={false}
              tick={{ fontSize: 12 }}
              padding={{ left: 20, right: 20 }}
              tickFormatter={(date: number) => format(new Date(date), 'MM/dd')}
            />
            <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={60} />
            <Tooltip content={renderTooltip} />
            {data
              .filter((dataPoint) => dataPoint.isSpotlight)
              .map((dataPoint) => {
                const oneDayMs = 24 * 60 * 60 * 1000;
                const startOfDay = dataPoint.reportDate - oneDayMs / 2;
                const endOfDay = dataPoint.reportDate + oneDayMs / 2;

                return (
                  <ReferenceArea
                    key={dataPoint.reportDate}
                    x1={startOfDay}
                    x2={endOfDay}
                    fill="#C5E2FF"
                    fillOpacity={0.5}
                  />
                );
              })}
            {lines.map(({ dataKey, color }) => (
              <Line
                key={dataKey}
                type="linear"
                dot={{ fill: '#fff', stroke: color, strokeWidth: 2, r: 4 }}
                activeDot={{ fill: color, stroke: '#fff', strokeWidth: 2, r: 6 }}
                dataKey={dataKey}
                stroke={color}
                strokeWidth={STROKE_WIDTH}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
